import React from "react";
import { motion } from "framer-motion";
import { Cpu, Network, Cctv, BatteryCharging, Printer, ArrowUpRight } from "lucide-react";
import { Reveal } from "../motion/Reveal";

const SOLUTIONS = [
  {
    key: "peripherals",
    title: "Computer Peripherals",
    body: "Enterprise desktops, laptops, monitors, keyboards and end-point accessories from HP, Dell and Lenovo — configured for scale.",
    Icon: Cpu,
    image: "https://images.pexels.com/photos/20694725/pexels-photo-20694725.jpeg",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    key: "networking",
    title: "Enterprise Networking",
    body: "Structured cabling, switches, routers, firewalls and Wi-Fi 6 rollouts across campuses and multi-site operations.",
    Icon: Network,
    image: "https://images.pexels.com/photos/17489152/pexels-photo-17489152.jpeg",
    span: "md:col-span-2",
  },
  {
    key: "cctv",
    title: "CCTV Surveillance",
    body: "Hikvision IP camera systems, NVRs, VMS integration, thermal & analytics — commissioned and audited.",
    Icon: Cctv,
    image: "https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg?w=1200",
    span: "",
  },
  {
    key: "ups",
    title: "UPS & Power Backup",
    body: "Online double-conversion UPS, load audits, battery banks and 24×7 site continuity engineering.",
    Icon: BatteryCharging,
    image: "https://images.pexels.com/photos/325229/pexels-photo-325229.jpeg?w=1200&q=80",
    span: "",
  },
  {
    key: "print",
    title: "Printers & Scanners",
    body: "MPS contracts, high-volume MFPs, document capture, and toner supply pipelines — nationwide.",
    Icon: Printer,
    image: "https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=1200&q=80",
    span: "md:col-span-2",
  },
];

const CardShell = ({ children, className = "", testid }) => (
  <div
    className={`solution-card group relative bg-[#FAFAFA] text-[#222222] rounded-xl overflow-hidden border border-[#9A9A9A]/15 ${className}`}
    data-testid={testid}
  >
    {children}
  </div>
);

export default function Solutions() {
  return (
    <section id="solutions" className="bg-[#444444] py-24 lg:py-32" data-testid="solutions-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 lg:mb-20">
          <Reveal>
            <div className="section-label mb-6">Our Solutions · 01 → 05</div>
            <h2 className="font-display text-[#FAFAFA] text-[40px] sm:text-[56px] lg:text-[76px] leading-[0.95] tracking-[-0.03em] max-w-[14ch]">
              End-to-end<br />
              <span className="text-[#FAFAFA]/60">enterprise</span> IT.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[#FAFAFA]/70 max-w-[42ch] leading-relaxed font-body">
              Five capability areas. One accountable partner. From boardroom laptops to
              multi-site surveillance, we design, procure, deploy and support without hand-offs.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 lg:gap-6 md:auto-rows-[280px]">
          {SOLUTIONS.map(({ key, title, body, Icon, image, span }, i) => (
            <Reveal key={key} delay={i * 0.08} className={span}>
              <CardShell className="h-full" testid={`solution-${key}`}>
                {image ? (
                  <div className="absolute inset-0">
                    <img src={image} alt={title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#222222]/85 via-[#222222]/40 to-transparent" />
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FAFAFA] to-[#efefef]" />
                )}
                <div className="relative h-full flex flex-col justify-between p-7 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${image ? "bg-[#FAFAFA]/95 text-[#A4161A]" : "bg-[#222222] text-[#A4161A]"} transition-colors duration-500 group-hover:bg-[#A4161A] group-hover:text-[#FAFAFA]`}>
                      <Icon size={20} strokeWidth={1.8} />
                    </div>
                    <ArrowUpRight
                      size={22}
                      className={`${image ? "text-[#FAFAFA]" : "text-[#9A9A9A]"} group-hover:text-[#A4161A] transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1`}
                    />
                  </div>
                  <div>
                    <div className={`text-[10px] tracking-[0.28em] uppercase font-btn mb-3 ${image ? "text-[#FAFAFA]/70" : "text-[#9A9A9A]"}`}>
                      {String(i + 1).padStart(2, "0")} · Solution
                    </div>
                    <h3 className={`font-display text-[22px] lg:text-[28px] leading-tight tracking-tight mb-3 ${image ? "text-[#FAFAFA]" : "text-[#222222]"}`}>
                      {title}
                    </h3>
                    <p className={`font-body text-[13px] lg:text-[14px] leading-relaxed max-w-[42ch] ${image ? "text-[#FAFAFA]/80" : "text-[#444444]"}`}>
                      {body}
                    </p>
                  </div>
                </div>
              </CardShell>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
